import React from 'react';
import { NavLink, useLocation } from 'react-router-dom';

import { Backdrop, Box, Button, Chip, IconButton } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';

import { Circle as BulletActive, CircleOutlined as BulletInactive, Close } from '@mui/icons-material';

import '../../assets/css/styles.sidebar.scss';
import FistoLogo from '../../assets/img/logo_s.png';
import { useFetchChequeCountsQuery } from '../../features/notifications/cheque/cheque-count-notification.api';
import { useFetchJournalCountsQuery } from '../../features/notifications/transactions/journal-count-notification.api';
import { useFetchTransactionCountsQuery } from '../../features/notifications/transactions/transactions-count-notification.api';
import { hideSidebar, toggleSidebar } from '../../features/sidebar/sidebar.slice';
import SidebarApplicationDropdown from './SidebarApplicationDropdown';
import SidebarApprovalDropdown from './SidebarApprovalDropdown';
import SidebarAuditDropdown from './SidebarAuditDropdown';
import SidebarChequeDropdown from './SidebarChequeDropdown';
import SidebarConfidentialDropdown from './SidebarConfidentialDropdown';
import SidebarDocumentsDropdown from './SidebarDocumentsDropdown';
import SidebarGeneralJournalDropdown from './SidebarGeneralJournalDropdown';
import SidebarMasterlistDropdown from './SidebarMasterlistDropdown';
import SidebarReleasingDropdown from './SidebarReleasingDropdown';
import SidebarReportsDropdown from './SidebarReportsDropdown';
import SidebarSettingsDropdown from './SidebarSettingsDropdown';
import SidebarVoucherDropdown from './SidebarVoucherDropdown';

const NO_PARAM = {};

const DisplayBubbleNotification = ({ notificationArray, permission, value }) => {
  const [count, setCount] = React.useState(0);

  React.useEffect(() => {
    const permittedNotification = notificationArray?.find((notification) => notification.permission.trim() === permission.trim());

    // console.log('permittedNotification:', permittedNotification);

    const newCount = permittedNotification?.result[value] || 0;

    setCount(newCount);
  }, [notificationArray, permission, value]);

  return <BubbleNotification count={count} />;
};

const BubbleNotification = ({ count }) => {
  return <Chip className={Boolean(count) ? `FstoSidebarChip-root` : `FstoSidebarChip-root hidden`} color="error" label={count > 99 ? '99+' : count} size="small" />;
};

const Sidebar = () => {
  const user = useSelector((state) => state.user);

  const open = useSelector((state) => state.sidebar);
  const color = useSelector((state) => state.theme);
  const location = useLocation();

  const [activeDropdown, setActiveDropdown] = React.useState('');

  React.useEffect(() => {
    setActiveDropdown(location?.pathname);
  }, [location]);

  const dispatch = useDispatch();

  const hideSidebarHandler = () => {
    dispatch(hideSidebar());
  };

  const toggleSidebarHandler = () => {
    dispatch(toggleSidebar());
  };

  const skipChequeNotification = !user?.permissions?.some((item) => [5, 6, 8, 16, 23, 24, 39].includes(item));
  const skipTransactionNotification = !user?.permissions?.some((item) => [1, 2, 3, 7, 11, 12, 13, 14, 15, 17, 19, 20, 25, 26, 27, 28, 30, 31].includes(item));
  const skipJournalNotification = !user?.permissions?.some((item) => [41, 48, 49, 50, 51, 52, 54, 55, 56, 61, 68, 69, 71, 75, 76, 77, 79, 85, 87].includes(item));

  const { data: cheque, isLoading: isChequeLoading } = useFetchChequeCountsQuery(NO_PARAM, {
    skip: skipChequeNotification,
    refetchOnMountOrArgChange: true,
    pollingInterval: 5 * (60 * 1000),
    skipPollingIfUnfocused: true,
  });

  const { data: transactions, isLoading: isTransactionsLoading } = useFetchTransactionCountsQuery(NO_PARAM, {
    skip: skipTransactionNotification,
    refetchOnMountOrArgChange: true,
    pollingInterval: 5 * (60 * 1000),
    skipPollingIfUnfocused: true,
  });

  const { data: journals, isLoading: isJournalsLoading } = useFetchJournalCountsQuery(NO_PARAM, {
    skip: skipJournalNotification,
    refetchOnMountOrArgChange: true,
    pollingInterval: 5 * (60 * 1000),
    skipPollingIfUnfocused: true,
  });

  const shouldDisplayDotNotification = (notificationArray, permission) => {
    if (notificationArray?.length) {
      const permittedNotification = notificationArray.find(({ permission: perm }) => perm.trim() === permission.trim());

      return permittedNotification?.result.pending || permittedNotification?.result.return;
    }
    return false;
  };

  const RouterLink = ({ children, ...rest }) => {
    return (
      <Button className="FstoButton-root" size="small" onClick={hideSidebarHandler} fullWidth disableElevation>
        <NavLink end {...rest}>
          {({ isActive }) => {
            return (
              <React.Fragment>
                {isActive ? (
                  <BulletActive className="FstoSidebarIcon-root" sx={{ ml: '4px', mr: '10px' }} />
                ) : (
                  <BulletInactive className="FstoSidebarIcon-root" sx={{ ml: '4px', mr: '10px' }} />
                )}
                {children}
              </React.Fragment>
            );
          }}
        </NavLink>
      </Button>
    );
  };

  return (
    <React.Fragment>
      <aside className={open ? `FstoSidebar-root visible ${color}` : `FstoSidebar-root ${color}`}>
        <Box className="FstoSidebar-wrapper">
          <Box className="FstoSidebar-toggle">
            <IconButton onClick={toggleSidebarHandler} disableRipple>
              <Close htmlColor="#f0f0e1" />
            </IconButton>
          </Box>
          <img className="FstoSidebar-logo" src={FistoLogo} alt="Fistó App" />
          {
            // Masterlist
            !!user && !!user.role.match(/administrator/i) && user.permissions.includes(0) && (
              <SidebarMasterlistDropdown RouterLink={RouterLink} user={user} activeDropdown={activeDropdown} />
            )
          }
          {
            // Documents
            !!user && user.permissions.some((item) => [1, 19, 20, 21, 22, 23, 25, 53].includes(item)) && (
              <SidebarDocumentsDropdown
                user={user}
                activeDropdown={activeDropdown}
                transactions={transactions}
                cheque={cheque}
                shouldDisplayDotNotification={shouldDisplayDotNotification}
                isTransactionsLoading={isTransactionsLoading}
                isChequeLoading={isChequeLoading}
                RouterLink={RouterLink}
                DisplayBubbleNotification={DisplayBubbleNotification}
              />
            )
          }
          {
            // Voucher
            !!user && user.permissions.some((item) => [10, 11, 12, 30].includes(item)) && (
              <SidebarVoucherDropdown
                user={user}
                activeDropdown={activeDropdown}
                transactions={transactions}
                shouldDisplayDotNotification={shouldDisplayDotNotification}
                isTransactionsLoading={isTransactionsLoading}
                RouterLink={RouterLink}
                DisplayBubbleNotification={DisplayBubbleNotification}
              />
            )
          }
          {
            // Audit   //!!user.role.match(/audit associate|treasury associate/gi)

            !!user && user.permissions.some((item) => [3, 5, 36, 103].includes(item)) && (
              <SidebarAuditDropdown
                user={user}
                activeDropdown={activeDropdown}
                transactions={transactions}
                cheque={cheque}
                shouldDisplayDotNotification={shouldDisplayDotNotification}
                isTransactionsLoading={isTransactionsLoading}
                isChequeLoading={isChequeLoading}
                RouterLink={RouterLink}
                DisplayBubbleNotification={DisplayBubbleNotification}
              />
            )
          }
          {
            // Cheque
            !!user && user.permissions.some((item) => [7, 8, 9, 40].includes(item)) && (
              <SidebarChequeDropdown
                user={user}
                activeDropdown={activeDropdown}
                transactions={transactions}
                cheque={cheque}
                shouldDisplayDotNotification={shouldDisplayDotNotification}
                isTransactionsLoading={isTransactionsLoading}
                isChequeLoading={isChequeLoading}
                RouterLink={RouterLink}
                DisplayBubbleNotification={DisplayBubbleNotification}
              />
            )
          }
          {
            // Releasing
            !!user && user.permissions.some((item) => [6, 24].includes(item)) && (
              <SidebarReleasingDropdown
                user={user}
                activeDropdown={activeDropdown}
                cheque={cheque}
                shouldDisplayDotNotification={shouldDisplayDotNotification}
                isChequeLoading={isChequeLoading}
                RouterLink={RouterLink}
                DisplayBubbleNotification={DisplayBubbleNotification}
              />
            )
          }
          {
            // Application of Loan
            !!user && user.permissions?.some((item) => [31]?.includes(item)) && (
              <SidebarApplicationDropdown
                user={user}
                activeDropdown={activeDropdown}
                transactions={transactions}
                shouldDisplayDotNotification={shouldDisplayDotNotification}
                isTransactionsLoading={isTransactionsLoading}
                RouterLink={RouterLink}
                DisplayBubbleNotification={DisplayBubbleNotification}
              />
            )
          }
          {
            // ApprovalDisplayBubbleNotification
            !!user && !!user.permissions?.some((item) => [17, 39, 41, 48, 49, 50, 51, 52, 54, 55, 56, 61, 75, 76, 77, 79, 85, 87, 100, 102]?.includes(item)) && (
              <SidebarApprovalDropdown
                user={user}
                activeDropdown={activeDropdown}
                transactions={transactions}
                shouldDisplayDotNotification={shouldDisplayDotNotification}
                isTransactionsLoading={isTransactionsLoading}
                isChequeLoading={isChequeLoading}
                isJournalsLoading={isJournalsLoading}
                cheque={cheque}
                RouterLink={RouterLink}
                DisplayBubbleNotification={DisplayBubbleNotification}
                journals={journals}
              />
            )
          }
          {
            // Confidential
            !!user && user.permissions?.some((item) => [2, 15, 28, 13, 16, 14]?.includes(item)) && (
              <SidebarConfidentialDropdown
                user={user}
                activeDropdown={activeDropdown}
                transactions={transactions}
                cheque={cheque}
                shouldDisplayDotNotification={shouldDisplayDotNotification}
                isTransactionsLoading={isTransactionsLoading}
                isChequeLoading={isChequeLoading}
                RouterLink={RouterLink}
                DisplayBubbleNotification={DisplayBubbleNotification}
              />
            )
          }
          {
            // General Journal
            !!user && user.permissions?.some((item) => [34, 43, 44, 45, 46, 47, 57, 58, 59, 60, 66, 67, 72, 73, 74, 78, 80, 81, 82, 83, 84, 86, 99, 101]?.includes(item)) && (
              <SidebarGeneralJournalDropdown
                journals={journals}
                cheque={cheque}
                shouldDisplayDotNotification={shouldDisplayDotNotification}
                isJournalsLoading={isJournalsLoading}
                isChequeLoading={isChequeLoading}
                DisplayBubbleNotification={DisplayBubbleNotification}
                RouterLink={RouterLink}
                activeDropdown={activeDropdown}
                user={user}
              />
            )
          }
          {
            // Reports
            !!user && user.permissions?.some((item) => [4, 32, 37, 89, 90, 91, 92, 93, 94, 95, 96, 103]?.includes(item)) && (
              <SidebarReportsDropdown RouterLink={RouterLink} user={user} activeDropdown={activeDropdown} />
            )
          }
          {
            //System Settings
            !!user && user?.permissions?.some((item) => [42]?.includes(item)) && <SidebarSettingsDropdown activeDropdown={activeDropdown} user={user} RouterLink={RouterLink} />
          }
        </Box>
      </aside>

      <Backdrop className="FstoBackdrop-root" open={open} onClick={hideSidebarHandler}></Backdrop>
    </React.Fragment>
  );
};

export default Sidebar;
